// routes.js
import Dashboard from "layouts/dashboard";
import Tables from "layouts/tables";
import Billing from "layouts/billing";
import Notifications from "layouts/notifications";
import Profile from "layouts/profile";
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";
import Register from "layouts/authentication/sign-up/register";
import Tithes from "layouts/accounts/tithes";
import Offerings from "layouts/accounts/offerings";
import Member from "layouts/members";
import SMS from "layouts/sms";
import DepartmentMembers from "layouts/department-members";
import Department from "layouts/departments";
import Attendance from "layouts/attendance";
import Biometric from "layouts/biometric";
import User from "layouts/users";
import Welfare from "layouts/accounts/welfare";
import MinistryMember from "layouts/ministry-member"; // Make sure this exists
import Ministry from "layouts/ministries";
// import Reports from "layouts/reports";
import MemberReport from "layouts/reports/member";
import WelfareReport from "layouts/reports/welfare";
import OfferingReport from "layouts/reports/offering";
import TitheReport from "layouts/reports/tithe";
import HarvestReport from "layouts/reports/harvest"; // Ensure this is the correct import
import AttendanceReport from "layouts/reports/attendance";
import SingleMember from "layouts/members/single-member";
import SingleTithe from "layouts/accounts/tithes/single-tithe";
import SingleWelfare from "layouts/accounts/welfare/single-welfare";

// @mui icons
import Icon from "@mui/material/Icon";

// Initialize with empty department routes
let departmentRoutes = [];
let ministryRoutes = [];

export const fetchDepartments = async (churchId) => {
  try {
    const response = await fetch(`https://api.ofi-cia.com/api/all-departments/${churchId}`);
    if (!response.ok) throw new Error("Network response was not ok");
    return await response.json();
  } catch (error) {
    console.error("Error fetching departments:", error);
    return [];
  }
};

export const loadDepartmentRoutes = async (churchId) => {
  try {
    const departments = await fetchDepartments(churchId);

    departmentRoutes = departments.map((dept) => ({
      name: dept.name,
      key: `department-${dept.id}`,
      icon: <Icon fontSize="small">groups</Icon>,
      route: `/departments/${dept.id}`,
      component: <DepartmentMembers departmentId={dept.id} />,
      protected: true,
      roles: ["admin", "staff"], // Match parent route roles
    }));

    return departmentRoutes;
  } catch (error) {
    console.error("Failed to load department routes:", error);
    return [];
  }
};

// Ministry functions (new)
export const fetchMinistries = async (churchId) => {
  try {
    const myHeaders = new Headers();
    myHeaders.append("Accept", "application/json");
    myHeaders.append("Authorization", "Bearer YOUR_TOKEN_HERE");
    myHeaders.append("Content-Type", "application/json");

    const response = await fetch(`https://api.ofi-cia.com/api/all-ministries/${churchId}`, {
      method: "GET",
      headers: myHeaders,
      redirect: "follow",
    });

    if (!response.ok) throw new Error("Network response was not ok");
    return await response.json();
  } catch (error) {
    console.error("Error fetching ministries:", error);
    return [];
  }
};

export const loadMinistryRoutes = async (churchId) => {
  try {
    const ministries = await fetchMinistries(churchId);

    ministryRoutes = ministries.map((min) => ({
      name: min.name,
      key: `ministry-${min.id}`,
      icon: <Icon fontSize="small">groups</Icon>,
      route: `/ministries/${min.id}`,
      component: <MinistryMember ministryId={min.id} />,
      protected: true,
      roles: ["admin", "staff"],
    }));

    return ministryRoutes;
  } catch (error) {
    console.error("Failed to load ministry routes:", error);
    return [];
  }
};

// Base routes configuration
export const baseRoutes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
    protected: true,
    // roles: ["admin"],
  },
  {
    type: "collapse",
    name: "Members",
    key: "members",
    icon: <Icon fontSize="small">group</Icon>,
    route: "/members",
    component: <Member />,
    protected: true,
    roles: ["admin", "staff"],
  },
  {
    type: "collapse",
    name: "Member",
    key: "member",
    icon: <Icon fontSize="small">group</Icon>,
    route: "/member",
    component: <SingleMember />,
    protected: true,
    roles: ["member"],
  },
  {
    type: "collapse",
    name: "Add Departments",
    key: "all-departments",
    icon: <Icon fontSize="small">group_add</Icon>,
    route: "/all-departments",
    component: <Department />,
    protected: true,
    roles: ["admin"],
  },
  {
    type: "collapse",
    name: "Church Departments",
    key: "departments",
    icon: <Icon fontSize="small">category</Icon>,
    route: "/departments", // Add a base route
    component: <DepartmentMembers />, // Main departments component
    protected: true,
    roles: ["admin", "staff"],
    collapse: [], // Will be populated dynamically
  },
  {
    type: "collapse",
    name: "Add Ministries",
    key: "all-ministries",
    icon: <Icon fontSize="small">group_add</Icon>,
    route: "/all-ministries",
    component: <Ministry />,
    protected: true,
    roles: ["admin"],
  },
  {
    type: "collapse",
    name: "Ministries",
    key: "ministries",
    icon: <Icon fontSize="small">diversity_3</Icon>,
    route: "/ministries",
    component: <MinistryMember />,
    protected: true,
    roles: ["admin", "staff"],
    collapse: [], // Will be populated dynamically
  },
  {
    type: "collapse",
    name: "Attendance",
    key: "attendance",
    icon: <Icon fontSize="small">how_to_reg</Icon>,
    route: "/attendance",
    component: <Attendance />,
    protected: true,
    roles: ["admin", "staff"],
  },
  {
    type: "collapse",
    name: "Biometric",
    key: "biometric",
    icon: <Icon fontSize="small">fingerprint</Icon>,
    route: "/biometric",
    component: <Biometric />,
    protected: true,
    roles: ["admin"],
  },
  {
    type: "collapse",
    name: "Tithe",
    key: "tithe",
    icon: <Icon fontSize="small">payments</Icon>,
    route: "/tithe",
    component: <SingleTithe />,
    protected: true,
    roles: ["member"],
  },
  {
    type: "collapse",
    name: "Welfare",
    key: "welfare",
    icon: <Icon fontSize="small">payments</Icon>,
    route: "/welfare",
    component: <SingleWelfare />,
    protected: true,
    roles: ["member"],
  },
  {
    type: "collapse",
    name: "Accounts",
    key: "accounts",
    icon: <Icon fontSize="small">attach_money</Icon>,
    collapse: [
      {
        name: "Tithes",
        key: "tithes",
        icon: <Icon fontSize="small">payments</Icon>,
        route: "/accounts/tithes",
        component: <Tithes />,
        protected: true,
        roles: ["admin", "treasurer"],
      },
      {
        name: "Welfare",
        key: "welfare",
        icon: <Icon fontSize="small">payments</Icon>,
        route: "/accounts/welfare",
        component: <Welfare />,
        protected: true,
        roles: ["admin", "treasurer"],
      },
      {
        name: "Offerings",
        key: "offerings",
        icon: <Icon fontSize="small">payments</Icon>,
        route: "/accounts/offerings",
        component: <Offerings />,
        protected: true,
        roles: ["admin", "treasurer"],
      },
    ],
    protected: true,
    roles: ["admin", "treasurer"],
  },
  {
    type: "collapse",
    name: "Message",
    key: "sms",
    icon: <Icon fontSize="small">textsms</Icon>,
    route: "/sms",
    component: <SMS />,
    protected: true,
    roles: ["admin", "staff", "treasurer"],
  },
  {
    type: "collapse",
    name: "Reports",
    key: "reports",
    icon: <Icon fontSize="small">assessment</Icon>,
    collapse: [
      {
        name: "Member Report",
        key: "member-report",
        icon: <Icon fontSize="small">people</Icon>,
        route: "/reports/member",
        component: <MemberReport />,
        protected: true,
        roles: ["admin"],
      },
      {
        name: "Attendance Report",
        key: "attendance-report",
        icon: <Icon fontSize="small">event_note</Icon>,
        route: "/reports/attendance",
        component: <AttendanceReport />,
        protected: true,
        roles: ["admin"],
      },
      // {
      //   name: "Welfare Report",
      //   key: "welfare-report",
      //   icon: <Icon fontSize="small">payments</Icon>,
      //   route: "/reports/welfare",
      //   component: <WelfareReport />,
      //   protected: true,
      //   roles: ["admin", "member"],
      // },
      // {
      //   name: "Offering Report",
      //   key: "offering-report",
      //   icon: <Icon fontSize="small">payments</Icon>,
      //   route: "/reports/offerings",
      //   component: <OfferingReport />,
      //   protected: true,
      //   roles: ["admin"],
      // },
      // {
      //   name: "Tithe Report",
      //   key: "tithe-report",
      //   icon: <Icon fontSize="small">payments</Icon>,
      //   route: "/reports/tithe",
      //   component: <TitheReport />,
      //   protected: true,
      //   roles: ["admin"],
      // },
      {
        name: "Welfare",
        key: "welfare",
        icon: <Icon fontSize="small">payments</Icon>,
        route: "/reports/welfare",
        component: <WelfareReport />,
        protected: true,
        roles: ["admin"],
      },
      {
        name: "Offerings",
        key: "offerings",
        icon: <Icon fontSize="small">payments</Icon>,
        route: "/reports/offerings",
        component: <OfferingReport />,
        protected: true,
        roles: ["admin"],
      },
      {
        name: "Tithes",
        key: "tithes",
        icon: <Icon fontSize="small">payments</Icon>,
        route: "/reports/tithe",
        component: <TitheReport />,
        protected: true,
        roles: ["admin"],
      },
      {
        name: "Harvests",
        key: "harvests",
        icon: <Icon fontSize="small">payments</Icon>,
        route: "/reports/harvest",
        component: <HarvestReport />,
        protected: true,
        roles: ["admin"],
      },
    ],
    protected: true,
    roles: ["admin"],
  },
  // {
  //   type: "collapse",
  //   name: "Reports",
  //   key: "billing",
  //   icon: <Icon fontSize="small">assessment</Icon>,
  //   route: "/billing",
  //   component: <Billing />,
  //   protected: true,
  //   roles: ["admin"],
  // },
  {
    type: "collapse",
    name: "Users Account",
    key: "user",
    icon: <Icon fontSize="small">lock</Icon>,
    route: "/user",
    component: <User />,
    protected: true,
    roles: ["admin"],
  },
  {
    type: "collapse",
    name: "Profile",
    key: "profile",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/profile",
    component: <Profile />,
    protected: true,
    roles: ["admin"],
  },
  {
    type: "hidden",
    name: "Sign In",
    key: "sign-in",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/authentication/sign-in",
    component: <SignIn />,
    protected: false,
  },
  {
    type: "hidden",
    name: "Sign Up",
    key: "sign-up",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/authentication/sign-up",
    component: <SignUp />,
  },
  {
    type: "hidden",
    name: "Register",
    key: "register",
    route: "/authentication/register/:churchId/:churchName",
    component: <Register />,
  },
];

// Function to get current routes with latest department data
export const getRoutes = () => {
  return baseRoutes.map((route) => {
    if (route.key === "departments") {
      return {
        ...route,
        collapse:
          departmentRoutes.length > 0
            ? departmentRoutes
            : [
                {
                  name: "No Departments",
                  key: "no-departments",
                  icon: <Icon fontSize="small">info</Icon>,
                  route: "#",
                  component: <div>No departments available</div>,
                  protected: true,
                },
              ],
      };
    }
    if (route.key === "ministries") {
      return {
        ...route,
        collapse:
          ministryRoutes.length > 0
            ? ministryRoutes
            : [
                {
                  name: "No Ministries",
                  key: "no-ministries",
                  icon: <Icon fontSize="small">info</Icon>,
                  route: "#",
                  component: <div>No ministries available</div>,
                  protected: true,
                },
              ],
      };
    }
    return route;
  });
};

// Filter out hidden routes for sidenav
export const getSidenavRoutes = () => {
  return getRoutes()
    .filter((route) => route.type !== "hidden")
    .map((route) => {
      if (route.collapse) {
        return {
          ...route,
          collapse: route.collapse.map((item) => ({
            ...item,
            component: item.component || null,
          })),
        };
      }
      return route;
    });
};
